import React, {
  useState,
  useCallback,
  useContext,
  createContext,
  useMemo,
} from 'react';
import api from 'services';
import { appWarnings } from 'utils/appWarnings';
import { useNewToast } from 'hooks/newToast';

const RegColheitaPorPeContext = createContext({});

const success = {
  type: 'success',
};

export function RegColheitaPorPeProvider({ children }) {
  const { showToast } = useNewToast();

  const [regMdobrasColheitaPorPe, setRegMdobrasColheitaPorPe] = useState([]);
  const [regMdobrasColheitaPorPeLoading, setRegMdobrasColheitaPorPeLoading] =
    useState(false);
  const [regMdobraColheitaPorPeLoading, setRegMdobraColheitaPorPeLoading] =
    useState(false);
  const [
    removeRegMdobrasColheitaPorPeLoading,
    setRemoveRegMdobrasColheitaPorPeLoading,
  ] = useState(false);

  const getRegMdobrasColheitaPorPe = useCallback(async (registroId) => {
    try {
      setRegMdobrasColheitaPorPeLoading(true);
      const response = await api
        .regAtividade()
        .getRegMdobrasColheitaPorPe(registroId);
      setRegMdobrasColheitaPorPe(response);
      setRegMdobrasColheitaPorPeLoading(false);
    } catch (errors) {
      setRegMdobrasColheitaPorPeLoading(false);
    }
  }, []);

  const insertRegColheitaPorPe = useCallback(
    async (registroId, data) => {
      try {
        setRegMdobraColheitaPorPeLoading(true);
        const formattedData = {
          vinculos: data?.map((item) => ({
            ...item,
            // Para colheita por pé, não temos tara/medida, apenas quantidade
            resto: 0, // Sempre 0 para colheita por pé
            tipo: 'UN', // Sempre UN (unidade) para colheita por pé
            medida: item?.medida.value || 'Planta',
            valor: item?.valor,
            colaborador: item?.duplicate
              ? item?.glo_vinculo_id
              : item?.colaborador,
            colhido: item?.colhido,
          })),
        };
        await api
          .regAtividade()
          .insertRegColheitaPorPe(registroId, formattedData);
        getRegMdobrasColheitaPorPe(registroId);
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setRegMdobraColheitaPorPeLoading(false);
      }
    },
    [showToast, getRegMdobrasColheitaPorPe],
  );

  const removeRegMdobrasColheitaPorPe = useCallback(
    async (activityId, id) => {
      try {
        setRemoveRegMdobrasColheitaPorPeLoading(true);
        await api.regAtividade().removeRegColheitaPorPe(activityId);
        if (id) {
          showToast({
            ...success,
            message: 'Colaborador excluído com sucesso!',
          });
        } else {
          showToast({
            ...success,
            message: 'Custo de colheita por pé excluído com sucesso!',
          });
        }
      } catch (err) {
        showToast(appWarnings.Danger(err));
        throw new Error(err);
      } finally {
        setRemoveRegMdobrasColheitaPorPeLoading(false);
      }
    },
    [showToast],
  );

  return (
    <RegColheitaPorPeContext.Provider
      value={useMemo(
        () => ({
          regMdobrasColheitaPorPe,
          regMdobrasColheitaPorPeLoading,
          regMdobraColheitaPorPeLoading,
          removeRegMdobrasColheitaPorPeLoading,
          getRegMdobrasColheitaPorPe,
          insertRegColheitaPorPe,
          removeRegMdobrasColheitaPorPe,
        }),
        [
          regMdobrasColheitaPorPe,
          regMdobrasColheitaPorPeLoading,
          regMdobraColheitaPorPeLoading,
          removeRegMdobrasColheitaPorPeLoading,
          getRegMdobrasColheitaPorPe,
          insertRegColheitaPorPe,
          removeRegMdobrasColheitaPorPe,
        ],
      )}
    >
      {children}
    </RegColheitaPorPeContext.Provider>
  );
}

export function useRegColheitaPorPe() {
  const context = useContext(RegColheitaPorPeContext);
  if (!context) {
    throw new Error(
      'useRegColheitaPorPe must be used within a RegColheitaPorPeProvider',
    );
  }
  return context;
}
